import { useState } from 'react';
import { CheckCircleIcon, LinkIcon, PresentationChartBarIcon } from '@heroicons/react/24/outline';
import { ROLE_SI_ADMIN, useAuth } from '@/contexts/AuthContext';
import ApresentacaoViewer from './onboarding/ApresentacaoViewer';
import PresencaOnboardingResumo from './onboarding/PresencaOnboardingResumo';
import { rascunhoDaApresentacao, type RascunhoSessaoOnboarding } from './onboarding/onboardingPresenca';
import {
  categoriaDocumento,
  cienciaCobradaDe,
  cienciaEmDia,
  millis,
  situacaoPrazo,
  textoPrazo,
} from '../publicacoes/publicacao.definitions';
import type { MeuEstadoPublicacao, Publicacao } from '../publicacoes/publicacao.types';
import { usePublicacaoLeitorMutations } from '../publicacoes/usePublicacoes';
import { useAnexosAbertos } from '../publicacoes/anexosAbertos';

interface DocumentoCardProps {
  documento: Publicacao;
  estado: MeuEstadoPublicacao | undefined;
  colaboradorId: string | null | undefined;
  agora: number;
  /** Destaca o card (veio de um link direto / pendência). */
  destacado?: boolean;
}

/** Palavra em destaque no aviso de ciência ("ABRA", "LEIA"). */
export function DestaqueAviso({ children }: { children: string }) {
  return (
    <strong className="font-bold uppercase text-amber-950">
      {children}
    </strong>
  );
}

const BOTAO ='rounded-full border border-raro-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-raro-gray-700 hover:border-primary-300 hover:text-primary-700';

/** Card da aba Documentos: abrir o arquivo (marca como lido) e "Li e estou ciente" quando cobrado. */
export function DocumentoCard({ documento, estado, colaboradorId, agora, destacado = false }: DocumentoCardProps) {
  const { marcarLida, confirmarCiencia } = usePublicacaoLeitorMutations(documento.id);
  const categoria = categoriaDocumento(documento.categoria);
  const atualizado = millis(documento.anunciadoEm) ?? millis(documento.publicarEm);
  const cobrada = cienciaCobradaDe(documento, colaboradorId);
  const ciente = cienciaEmDia(documento, estado);
  const situacao = situacaoPrazo(documento, agora);
  const arquivo = documento.arquivo;
  // "Li e estou ciente" só depois de abrir o arquivo (fica registrado como lido) — senão tem gente
  // que confirma sem nem abrir.
  const { abertos, marcar } = useAnexosAbertos(documento.id);
  const anexos = documento.anexos ?? [];
  const faltamAnexos = anexos.filter((a) => !abertos.includes(a.path)).length;
  const abriu = estado?.lida === true && faltamAnexos === 0;
  // Apresentação HTML (ex.: onboarding de SI): abre só dentro do SI — sem "nova aba", pra o
  // registro de início/fim e o lembrete da sessão sempre valerem. SI "Apresentar"; demais "Ver apresentação".
  const { userHasRole } = useAuth();
  const ehSI = userHasRole(ROLE_SI_ADMIN);
  const ehApresentacao = arquivo?.tipo !== 'link' && arquivo?.contentType === 'text/html';
  const [apresentandoDesde, setApresentandoDesde] = useState<Date | null>(null);
  const [rascunhoSessao, setRascunhoSessao] = useState<RascunhoSessaoOnboarding | null>(null);
  const registraPresenca = ehSI && documento.categoria === 'onboarding';
  const abrirApresentacao = () => {
    marcarLida.mutate();
    setApresentandoDesde(new Date());
  };
  // Lembrete do registro: o SI fechou a apresentação de onboarding depois de alguns minutos →
  // o registro abre sozinho, com data/horários e as admissões que ainda não fizeram.
  const fecharApresentacao = (forcarRegistro = false) => {
    const inicio = apresentandoDesde;
    setApresentandoDesde(null);
    if (!registraPresenca || !inicio) return;
    const rascunho = rascunhoDaApresentacao(inicio, new Date(), forcarRegistro);
    if (rascunho) setRascunhoSessao(rascunho);
  };

  const selo = cobrada
    ? ciente
      ? { texto: 'Ciente ✓', classe: 'text-xs font-semibold text-green-700' }
      : situacao.tipo === 'atrasada'
        ? { texto: textoPrazo(situacao), classe: 'rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-700' }
        : {
            texto: situacao.tipo === 'vence' ? textoPrazo(situacao) : 'Confirmar ciência',
            classe: 'rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800',
          }
    : documento.ciencia?.exige
      ? null
      : { texto: 'Sem ciência', classe: 'text-xs text-raro-gray-400' };

  return (
    <article
      id={`documento-${documento.id}`}
      data-tour="si-doc-card"
      className={`flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm ${
        destacado ? 'border-primary-300 ring-2 ring-primary-100' : 'border-raro-gray-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl text-lg ${categoria.fundo}`} aria-hidden="true">
          {categoria.icone}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-montserrat text-base font-semibold text-raro-gray-900">{documento.titulo}</h3>
            {selo && <span className={`shrink-0 ${selo.classe}`}>{selo.texto}</span>}
          </div>
          <p className="text-xs text-raro-gray-500">
            {categoria.label} · <b>versão {documento.versao ?? 1}</b>
            {atualizado ? ` · atualizado em ${new Date(atualizado).toLocaleDateString('pt-BR')}` : ''}
          </p>
        </div>
      </div>
      {documento.resumo && <p className="text-sm text-raro-gray-600">{documento.resumo}</p>}
      {documento.oQueMudou && (documento.versao ?? 1) > 1 && (
        <p className="rounded-lg bg-primary-50/60 px-3 py-2 text-xs text-primary-800">
          <b>O que mudou na v{documento.versao}:</b> {documento.oQueMudou}
        </p>
      )}
      {anexos.length > 0 && (
        <ul className="space-y-1">
          {anexos.map((anexo) => (
            <li key={anexo.path}>
              <a
                href={anexo.url}
                target="_blank"
                rel="noreferrer"
                onClick={() => marcar(anexo.path)}
                className="flex items-center gap-2 rounded-lg border border-raro-gray-200 px-3 py-1.5 text-xs hover:border-primary-200"
              >
                <span aria-hidden="true">📎</span>
                <span className="min-w-0 flex-1 truncate">{anexo.nome}</span>
                {abertos.includes(anexo.path) ? (
                  <span className="font-semibold text-green-700">Aberto ✓</span>
                ) : (
                  <span className="font-semibold text-primary-700">Abrir</span>
                )}
              </a>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-dashed border-primary-200 pt-3" data-tour="si-doc-acoes">
        {arquivo && ehApresentacao && (
          <button
            type="button"
            onClick={abrirApresentacao}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
          >
            <PresentationChartBarIcon className="h-4 w-4" aria-hidden="true" />
            {ehSI ? 'Apresentar' : 'Ver apresentação'}
          </button>
        )}
        {arquivo && !ehApresentacao && (
          <a href={arquivo.url} target="_blank" rel="noreferrer" className={`${BOTAO} inline-flex items-center gap-1.5`} onClick={() => marcarLida.mutate()}>
            {arquivo.tipo === 'link' && <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />}
            {arquivo.tipo === 'link' ? 'Abrir link' : arquivo.contentType?.startsWith('image/') ? 'Abrir imagem' : 'Abrir PDF'}
          </a>
        )}
        {cobrada &&
          (ciente ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
              <CheckCircleIcon className="h-4 w-4" aria-hidden="true" /> Você confirmou a ciência
            </span>
          ) : (
            <button
              type="button"
              className="rounded-full bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-raro-gray-300"
              disabled={!abriu || confirmarCiencia.isPending}
              title={abriu ? undefined : 'Abra o documento antes de confirmar'}
              onClick={() => confirmarCiencia.mutate(documento.ciencia.versao)}
            >
              {confirmarCiencia.isPending ? 'Confirmando...' : 'Li e estou ciente'}
            </button>
          ))}
      </div>
      {ehSI && ehApresentacao && documento.categoria === 'onboarding' && (
        <PresencaOnboardingResumo
          documentoId={documento.id}
          versao={documento.versao ?? 1}
          rascunho={rascunhoSessao}
          onRascunhoConsumido={() => setRascunhoSessao(null)}
        />
      )}
      {apresentandoDesde && arquivo && (
        <ApresentacaoViewer
          url={arquivo.url}
          titulo={documento.titulo}
          versao={documento.versao ?? 1}
          onFechar={() => fecharApresentacao()}
          acaoExtra={
            registraPresenca ? { label: 'Registrar esta sessão', onClick: () => fecharApresentacao(true) } : undefined
          }
        />
      )}
      {cobrada && !ciente && !abriu && (
        <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
          <span aria-hidden="true">⚠️</span>
          <span>
            <DestaqueAviso>ABRA</DestaqueAviso> e <DestaqueAviso>LEIA</DestaqueAviso> o documento
            {anexos.length > 0 ? ` e ${anexos.length === 1 ? 'o anexo' : 'os anexos'}` : ''} antes de clicar em &quot;Li e estou
            ciente&quot;.
          </span>
        </p>
      )}
    </article>
  );
}
